/* One table's profile: how its baseline was reached, what a profile can and cannot say about it, every
 * column at a glance, and what moved since the previous profile.
 *
 * **Its own address**, `/profiles/:datasetId`, so a table somebody was sent opens on the page they were
 * sent to and the breadcrumb back to the estate is a real parent.
 *
 * **Frequent values are shown by rank and share only.** The API returns salted digests and counts, and
 * no value; a digest tells a reader nothing they can act on, so neither is shown.
 */

import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, generatePath, useParams } from 'react-router-dom';

import { useConnections } from '@/entities/connection';
import { can, PROFILE_DATASETS_PERMISSION, useIdentity } from '@/entities/session';
import { useDataset, useDatasetFacts } from '@/features/catalog-browser';
import {
  driftVerdict,
  isDominated,
  share,
  useRequestProfile,
  useSketchDrift,
  useSketches,
} from '@/features/profiler';
import type {
  ColumnDrift,
  ColumnSketch,
  DatasetSketches,
  SketchDrift,
} from '@/shared/api/generated/catalog';
import { ageText } from '@/shared/model/age';
import { compactNumber } from '@/shared/model/number';
import { SCREEN_PATHS } from '@/shared/routes';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { DatasetObjectTypeIcon } from '@/shared/ui/DatasetObjectTypeIcon';
import { EmptyState } from '@/shared/ui/EmptyState';
import { HelpLabel } from '@/shared/ui/InfoTip';

import {
  CARDINALITY_LABEL_KEYS,
  baselineBand,
  baselineTone,
  cardinalityLevel,
  completenessDelta,
  completenessSlope,
  completenessTone,
  distinctText,
  freshnessOf,
  waterfall,
} from '../model/estate';
import { ColumnShape, CompletenessChange } from './ColumnCharts';
import { scrollToStart } from './scroll';
import { FILL_VAR, FRESHNESS_TONE, VERDICT_TONE } from './tones';

/** The six dimensions of data quality and what a profile alone can say about each. A profile measures
 * completeness and uniqueness; the rest need a rule, and accuracy needs something to compare with. */
const DIMENSIONS = [
  ['completeness', 'measured', 'good'],
  ['uniqueness', 'measured', 'good'],
  ['validity', 'needsRule', 'watch'],
  ['consistency', 'needsRule', 'watch'],
  ['timeliness', 'needsRule', 'watch'],
  ['accuracy', 'needsReference', 'none'],
] as const;

const FREQUENT_SHOWN = 5;

export function ProfileDetail(): ReactElement {
  const { t } = useTranslation();
  const { datasetId = '' } = useParams<{ datasetId: string }>();
  const dataset = useDataset(datasetId);
  const facts = useDatasetFacts([datasetId]);
  const sketches = useSketches(datasetId);
  const drift = useSketchDrift(datasetId);
  const connections = useConnections();
  const identity = useIdentity();
  const run = useRequestProfile(datasetId);
  const [openColumn, setOpenColumn] = useState<string | null>(null);
  const [now] = useState(() => new Date());
  const topRef = useRef<HTMLElement | null>(null);

  /* Open at the top. The shell's content pane keeps its scroll across navigation, so a table opened
     from the bottom of the estate would otherwise land scrolled to the bottom of its own page. */
  useEffect(() => {
    scrollToStart(topRef.current);
  }, [datasetId]);

  const canRun = can(identity.data, PROFILE_DATASETS_PERMISSION);
  const runTitle = canRun
    ? t('profiles.run.title')
    : t('profiles.run.needs', { permission: PROFILE_DATASETS_PERMISSION });

  const name = dataset.isSuccess
    ? `${dataset.data.schema_name}.${dataset.data.object_name}`
    : datasetId;
  const own = facts.data?.get(datasetId);
  const freshness = freshnessOf(own, now);
  const age =
    own?.last_profiled_at === undefined || own.last_profiled_at === null
      ? null
      : (ageText(t, own.last_profiled_at, now) ?? own.last_profiled_at);
  const connection = dataset.isSuccess
    ? (connections.data?.items.find((each) => each.id === dataset.data.connection_id)
        ?.name ?? t('profiles.unlistedSource'))
    : null;
  const rowsAt = own?.rows_at_last_profile ?? null;

  function revealColumn(column: string): void {
    setOpenColumn(column);
    /* The row is below the fold on a wide table, and the reader just asked for it. */
    window.requestAnimationFrame(() => {
      const row = document.getElementById(columnRowId(column));
      if (row !== null && 'scrollIntoView' in row) {
        row.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    });
  }

  return (
    <section className="lq-pf">
      <nav className="lq-pf__crumbs" aria-label={t('breadcrumbs.label')} ref={topRef}>
        <Link to={SCREEN_PATHS.profiles}>{t('profiles.title')}</Link>
        <span aria-hidden="true">›</span>
        <span className="lq-pf__crumbName" aria-current="page">
          {name}
        </span>
      </nav>

      <div className="lq-pf__head">
        <div className="lq-pf__titles lq-pf__titles--detail">
          <div className="lq-otype-title">
            {dataset.isSuccess ? (
              <DatasetObjectTypeIcon objectType={dataset.data.object_type} />
            ) : null}
            <h1 className="lq-pf__detailTitle">{name}</h1>
          </div>
          <div className="lq-pf__chips">
            <span className="lq-pf__fresh" data-tone={FRESHNESS_TONE[freshness]}>
              <span className="lq-pf__freshDot" />
              {age === null
                ? t('profiles.freshness.never')
                : t('profiles.detail.fresh', {
                    label: t(`profiles.freshness.${freshness}`),
                    age,
                  })}
            </span>
            {connection !== null && <span className="lq-pf__chip">{connection}</span>}
            {dataset.isSuccess && (
              <span className="lq-pf__chip">
                {t('profiles.detail.version', { version: dataset.data.current_version })}
              </span>
            )}
            {sketches.isSuccess && sketches.data !== null && (
              <span className="lq-pf__chip">
                {t('profiles.detail.columns', { count: sketches.data.columns.length })}
              </span>
            )}
            {rowsAt !== null && (
              <span className="lq-pf__chip">
                {rowsAt === 0
                  ? t('profiles.card.empty')
                  : t('profiles.detail.rows', { rows: compactNumber(rowsAt) })}
              </span>
            )}
          </div>
        </div>
        <Link
          className="lq-btn lq-btn--secondary"
          to={generatePath(SCREEN_PATHS.dataset, { datasetId })}
        >
          {t('profiles.detail.open')}
        </Link>
        <Button
          tone="primary"
          disabled={!canRun || run.isPending || run.isSuccess}
          title={runTitle}
          onClick={() => {
            run.mutate();
          }}
        >
          {run.isSuccess ? t('profiles.detail.requested') : t('profiles.detail.run')}
        </Button>
      </div>

      {run.isError && (
        <Alert tone="error" title={t('profiles.runAll.failed', { count: 1 })}>
          {run.error.message}
        </Alert>
      )}
      {dataset.isError && (
        <Alert tone="error" title={t('profiles.detail.missing')}>
          {dataset.error.message}
        </Alert>
      )}
      {sketches.isError && (
        <Alert tone="error" title={t('profiles.detail.failed')}>
          {sketches.error.message}
        </Alert>
      )}

      {sketches.isPending && (
        <span className="lq-skeleton__bar lq-pf__skeleton lq-pf__skeleton--detail" />
      )}

      {sketches.isSuccess && sketches.data === null && (
        <EmptyState
          title={t('profiles.detail.never.title')}
          guidance={t('profiles.detail.never.guidance')}
        />
      )}

      {sketches.isSuccess && sketches.data !== null && (
        <>
          <div className="lq-pf__detailGrid">
            <WaterfallCard sketches={sketches.data} />
            <DimensionsCard />
          </div>
          <div className="lq-pf__chartRow">
            <ColumnShape columns={sketches.data.columns} onOpen={revealColumn} />
            <CompletenessChange
              columns={sketches.data.columns}
              drift={drift.isSuccess ? (drift.data?.columns ?? null) : null}
              onOpen={revealColumn}
            />
          </div>
          <div className="lq-pf__mapRow">
            <ColumnMap
              sketches={sketches.data}
              drift={drift.isSuccess ? drift.data : null}
              openColumn={openColumn}
              onToggle={(column) => {
                setOpenColumn(openColumn === column ? null : column);
              }}
            />
            <ChangesPanel
              drift={drift.isSuccess ? drift.data : null}
              pending={drift.isPending}
              onOpen={revealColumn}
            />
          </div>
        </>
      )}
    </section>
  );
}

function columnRowId(column: string): string {
  return `lq-pf-column-${column}`;
}

/* ------------------------------------------------------------------ the baseline waterfall */

function WaterfallCard({
  sketches,
}: {
  readonly sketches: DatasetSketches;
}): ReactElement {
  const { t } = useTranslation();
  const baseline = sketches.baseline;
  const steps = waterfall(baseline);

  return (
    <article className="lq-pf__panel">
      <div className="lq-pf__panelHead lq-pf__panelHead--center">
        <HelpLabel
          subject={t('profiles.waterfall.title')}
          help={t('help.metric.profileBaseline')}
          className="lq-infotip-row lq-infotip-row--grow"
        >
          <h2 className="lq-pf__panelTitle">{t('profiles.waterfall.title')}</h2>
        </HelpLabel>
        {baseline.value !== null && (
          <span className="lq-pf__baselinePill" data-tone={baselineTone(baseline.value)}>
            {baseline.value}
          </span>
        )}
      </div>
      {steps === null || baseline.value === null ? (
        <div className="lq-pf__noBaseline">
          <p className="lq-pf__noBaselineText">
            {t('profiles.waterfall.none', {
              unmeasured: baseline.unmeasured_columns.length,
              total: sketches.columns.length,
            })}
          </p>
        </div>
      ) : (
        <>
          <div className="lq-pf__falls">
            {steps.map((step, index) => {
              const label =
                step.kind === 'start'
                  ? t('profiles.waterfall.start')
                  : step.kind === 'baseline'
                    ? t('profiles.waterfall.baseline')
                    : (step.column ?? '');
              const sub =
                step.kind === 'start'
                  ? t('profiles.waterfall.startSub')
                  : step.kind === 'baseline'
                    ? t(`profiles.waterfall.band.${baselineBand(step.figure)}`)
                    : t(`profiles.finding.${step.finding ?? 'sparse'}`);
              return (
                <div
                  // Steps have no identity beyond their place: two findings may share a column.
                  key={index}
                  className="lq-pf__fall"
                  data-tone={step.tone}
                >
                  <div className="lq-pf__fallPlot">
                    <div
                      className="lq-pf__fallBar"
                      style={{
                        bottom: `${String(step.bottom)}%`,
                        height: `${String(step.height)}%`,
                      }}
                    />
                    <div
                      className="lq-pf__fallFigure"
                      style={{ bottom: `${String(step.bottom + step.height)}%` }}
                    >
                      {step.kind === 'deduction'
                        ? t('profiles.waterfall.deduction', { points: step.figure })
                        : step.figure}
                    </div>
                  </div>
                  <div className="lq-pf__fallLabel" title={`${label} · ${sub}`}>
                    <div
                      className={
                        step.kind === 'deduction'
                          ? 'lq-pf__fallName lq-pf__fallName--column'
                          : 'lq-pf__fallName'
                      }
                    >
                      {label}
                    </div>
                    <div className="lq-pf__fallSub">{sub}</div>
                  </div>
                </div>
              );
            })}
          </div>
          <p className="lq-pf__fallFoot">
            {t('profiles.waterfall.deducted', {
              points: 100 - baseline.value,
              count: baseline.findings.length,
            })}
          </p>
        </>
      )}
    </article>
  );
}

/* ------------------------------------------------------------------ dimensions */

function DimensionsCard(): ReactElement {
  const { t } = useTranslation();
  return (
    <article className="lq-pf__panel">
      <h2 className="lq-pf__panelTitle">{t('profiles.dims.title')}</h2>
      <div className="lq-pf__dims">
        {DIMENSIONS.map(([dimension, state, tone]) => (
          <div key={dimension} className="lq-pf__dim" data-tone={tone}>
            <span className="lq-pf__dimDot" />
            <div className="lq-pf__dimName">{t(`profiles.dims.${dimension}`)}</div>
            <div className="lq-pf__dimState">{t(`profiles.dims.${state}`)}</div>
          </div>
        ))}
      </div>
    </article>
  );
}

/* ------------------------------------------------------------------ the column map */

function ColumnMap({
  sketches,
  drift,
  openColumn,
  onToggle,
}: {
  readonly sketches: DatasetSketches;
  readonly drift: SketchDrift | null;
  readonly openColumn: string | null;
  readonly onToggle: (column: string) => void;
}): ReactElement {
  const { t } = useTranslation();
  const driftByColumn = new Map(
    (drift?.columns ?? []).map((each) => [each.column, each]),
  );

  return (
    <article className="lq-pf__map">
      <div className="lq-pf__mapHead">
        <h2 className="lq-pf__panelTitle lq-pf__grow">{t('profiles.map.title')}</h2>
        <span className="lq-pf__keys">
          {(['good', 'watch', 'risk', 'none'] as const).map((band) => (
            <span key={band} className="lq-pf__key">
              <span className="lq-pf__keyDot" data-fill={band} />
              {t(`profiles.map.legend.${band}`)}
            </span>
          ))}
          <span className="lq-pf__key">
            <span className="lq-pf__keyTick" />
            {t('profiles.map.legend.previous')}
          </span>
        </span>
      </div>
      <div className="lq-pf__mapBody">
        <div className="lq-pf__mapCols" aria-hidden="true">
          <span />
          <span>{t('profiles.map.col.column')}</span>
          <span>{t('profiles.map.col.completeness')}</span>
          <span>{t('profiles.map.col.distinct')}</span>
          <span>{t('profiles.map.col.top')}</span>
          <span>{t('profiles.map.col.since')}</span>
        </div>
        {sketches.columns.map((sketch) => (
          <ColumnRow
            key={sketch.column}
            sketch={sketch}
            drift={driftByColumn.get(sketch.column)}
            open={openColumn === sketch.column}
            onToggle={() => {
              onToggle(sketch.column);
            }}
          />
        ))}
      </div>
    </article>
  );
}

function ColumnRow({
  sketch,
  drift,
  open,
  onToggle,
}: {
  readonly sketch: ColumnSketch;
  readonly drift: ColumnDrift | undefined;
  readonly open: boolean;
  readonly onToggle: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const tone = completenessTone(sketch.completeness);
  const verdict = drift === undefined ? null : driftVerdict(drift);
  const before = drift?.completeness_before ?? null;
  const delta = completenessDelta(before, sketch.completeness);
  const level = cardinalityLevel(sketch);
  const dominated = isDominated(sketch);
  const dominant = sketch.dominant_share;
  const detailId = `${columnRowId(sketch.column)}-detail`;

  return (
    <div
      id={columnRowId(sketch.column)}
      className={open ? 'lq-pf__col lq-pf__col--open' : 'lq-pf__col'}
    >
      <button
        type="button"
        className="lq-pf__colRow"
        aria-expanded={open}
        aria-controls={detailId}
        onClick={onToggle}
      >
        <span className="lq-pf__chev" aria-hidden="true">
          {open ? '▾' : '▸'}
        </span>
        <span className="lq-pf__colName">
          <span className="lq-pf__colNameText">{sketch.column}</span>
          {dominated && (
            <span className="lq-pf__dominated" title={t('profiles.map.dominatedTitle')}>
              {t('profiles.map.dominated')}
            </span>
          )}
        </span>
        <span className="lq-pf__comp">
          <span className="lq-pf__compTrack">
            {sketch.completeness === null ? (
              <span className="lq-pf__compHatch" />
            ) : (
              <span
                className="lq-pf__compFill"
                data-fill={tone}
                data-grow=""
                style={{ width: `${(sketch.completeness * 100).toFixed(1)}%` }}
              />
            )}
            {delta !== null && before !== null && (
              <span
                className="lq-pf__compPrev"
                title={t('profiles.map.previous')}
                style={{ left: `${(before * 100).toFixed(1)}%` }}
              />
            )}
          </span>
          <span className="lq-pf__compText" data-tone={tone}>
            {sketch.completeness === null
              ? t('profiles.map.notMeasured')
              : share(sketch.completeness)}
          </span>
        </span>
        <span className="lq-pf__card2">
          <span className="lq-pf__cardSegs">
            {[1, 2, 3, 4, 5].map((step) => (
              <span
                key={step}
                className="lq-pf__cardSeg"
                data-fill={
                  level === 0
                    ? 'none'
                    : step <= level
                      ? level === 1
                        ? 'watch'
                        : 'teal'
                      : undefined
                }
              />
            ))}
          </span>
          <span className="lq-pf__cardText">
            {t('profiles.map.cardinality', {
              level: t(CARDINALITY_LABEL_KEYS[level]),
              distinct: sketch.completeness === null ? '—' : distinctText(sketch),
            })}
          </span>
        </span>
        <span className="lq-pf__dom">
          <span
            className="lq-pf__domRing"
            style={
              dominant === null
                ? undefined
                : {
                    background: `conic-gradient(${FILL_VAR[dominated ? 'watch' : 'teal']} 0 ${Math.max(2, dominant * 100).toFixed(1)}%, var(--pf-track-strong) ${Math.max(2, dominant * 100).toFixed(1)}% 100%)`,
                  }
            }
          >
            <span className="lq-pf__domHole" />
          </span>
          <span className="lq-pf__domText">{share(dominant)}</span>
        </span>
        <span className="lq-pf__since">
          <span
            className="lq-pf__verdict"
            data-tone={verdict === null ? 'none' : VERDICT_TONE[verdict]}
            data-hollow={verdict === null || verdict === 'unmeasurable'}
          >
            <span className="lq-pf__verdictDot" />
            {verdict === null
              ? t('profiles.verdict.notCompared')
              : t(`profiles.verdict.${verdict}`)}
          </span>
          {delta !== null && (
            <span className="lq-pf__delta" data-direction={delta < 0 ? 'down' : 'up'}>
              {t(delta < 0 ? 'profiles.map.delta.down' : 'profiles.map.delta.up', {
                points: Math.abs(delta).toFixed(1),
              })}
            </span>
          )}
        </span>
      </button>
      {open && (
        <div className="lq-pf__colDetail" id={detailId}>
          <div className="lq-pf__freq">
            <div className="lq-pf__eyebrow">{t('profiles.map.freq.title')}</div>
            {sketch.frequent_values.slice(0, FREQUENT_SHOWN).map((value, index) => (
              <div key={value.digest} className="lq-pf__freqRow">
                <span className="lq-pf__freqRank">
                  {t('profiles.map.freq.rank', { rank: index + 1 })}
                </span>
                <span className="lq-pf__freqTrack">
                  <span
                    className="lq-pf__freqFill"
                    data-fill={index === 0 && dominated ? 'watch' : 'teal'}
                    data-grow=""
                    style={{
                      width: `${Math.max(2, Math.min(100, value.share * 100)).toFixed(1)}%`,
                    }}
                  />
                </span>
                <span className="lq-pf__freqShare">{share(value.share)}</span>
              </div>
            ))}
            {sketch.frequent_values.length === 0 && (
              <p className="lq-pf__freqNote">{t('profiles.map.freq.none')}</p>
            )}
            {sketch.frequent_values_truncated && (
              <p className="lq-pf__freqNote lq-pf__freqNote--muted">
                {t('profiles.map.freq.truncated')}
              </p>
            )}
          </div>
          <dl className="lq-pf__facts">
            <dt>{t('profiles.map.detail.change')}</dt>
            <dd>
              {before === null || sketch.completeness === null
                ? t('profiles.map.detail.noPrevious')
                : t('profiles.map.detail.changeValue', {
                    before: share(before),
                    after: share(sketch.completeness),
                  })}
            </dd>
            <dt>{t('profiles.map.detail.nulls')}</dt>
            <dd>{sketch.null_count.toLocaleString()}</dd>
            <dt>{t('profiles.map.detail.rows')}</dt>
            <dd>{sketch.rows_scanned.toLocaleString()}</dd>
            <dt>{t('profiles.map.detail.distinct')}</dt>
            <dd>
              {sketch.exact
                ? t('profiles.map.detail.exact')
                : t('profiles.map.detail.estimate')}
            </dd>
          </dl>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ what changed */

function ChangesPanel({
  drift,
  pending,
  onOpen,
}: {
  readonly drift: SketchDrift | null;
  readonly pending: boolean;
  readonly onOpen: (column: string) => void;
}): ReactElement {
  const { t } = useTranslation();
  const changed =
    drift === null ? [] : drift.columns.filter((each) => driftVerdict(each) !== 'stable');

  return (
    <aside className="lq-pf__changes">
      <div className="lq-pf__panelHead lq-pf__panelHead--center">
        <HelpLabel
          subject={t('profiles.changes.title')}
          help={t('help.metric.drift')}
          className="lq-infotip-row lq-infotip-row--grow"
        >
          <h2 className="lq-pf__panelTitle">{t('profiles.changes.title')}</h2>
        </HelpLabel>
        {drift !== null && <span className="lq-pf__changeCount">{changed.length}</span>}
      </div>
      {!pending && drift === null && (
        <p className="lq-pf__changesNote">{t('profiles.changes.none')}</p>
      )}
      {drift !== null && changed.length === 0 && (
        <p className="lq-pf__allStable">{t('profiles.changes.allStable')}</p>
      )}
      {changed.map((each) => {
        const verdict = driftVerdict(each);
        const slope = completenessSlope(each);
        return (
          <button
            key={each.column}
            type="button"
            className="lq-pf__change"
            onClick={() => {
              onOpen(each.column);
            }}
          >
            <span className="lq-pf__changeHead">
              <span className="lq-pf__changeName">{each.column}</span>
              <span className="lq-pf__changeVerdict" data-tone={VERDICT_TONE[verdict]}>
                {t(`profiles.verdict.${verdict}`)}
              </span>
            </span>
            {slope !== null && (
              <span className="lq-pf__slope" data-fell={slope.fell} aria-hidden="true">
                <span className="lq-pf__slopeTrack" />
                <span
                  className="lq-pf__slopeLine"
                  style={{
                    left: `${(slope.low * 100).toFixed(1)}%`,
                    width: `${((slope.high - slope.low) * 100).toFixed(1)}%`,
                  }}
                />
                <span
                  className="lq-pf__slopeBefore"
                  style={{ left: `${(slope.before * 100).toFixed(1)}%` }}
                />
                <span
                  className="lq-pf__slopeAfter"
                  style={{ left: `${(slope.after * 100).toFixed(1)}%` }}
                />
              </span>
            )}
            <span className="lq-pf__changeText">
              {slope !== null
                ? t('profiles.changes.slope', {
                    before: share(slope.before),
                    after: share(slope.after),
                  })
                : t(`profiles.changes.${verdict === 'stable' ? 'moved' : verdict}`)}
            </span>
          </button>
        );
      })}
      {drift !== null &&
        drift.added_columns.length + drift.removed_columns.length > 0 && (
          <div className="lq-pf__schemaChanges">
            {drift.added_columns.map((column) => (
              <span key={`+${column}`} className="lq-pf__schemaChip" data-tone="good">
                + {column}
              </span>
            ))}
            {drift.removed_columns.map((column) => (
              <span key={`-${column}`} className="lq-pf__schemaChip" data-tone="risk">
                − {column}
              </span>
            ))}
          </div>
        )}
      {drift !== null && (
        <p className="lq-pf__computed">
          {t('profiles.changes.computed', { how: drift.computed_from })}
        </p>
      )}
    </aside>
  );
}
